import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    useContractTracking,
    TrackedContract,
} from "../contexts/ContractTrackingContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useContractSummary } from "../contexts/ContractSummaryContext";
import { useHistoricalModal } from "../contexts/HistoricalModalContext";
import { getApiUrl } from "../config/api";
import type { MmecScore, WhaleSummary } from "../types/contractAnalytics";
import { isDesignSystemOn } from "../theme/mmecDsPreview";
import { ToolState } from "./mmec/ToolState";
import "./TrackedContractsView.ds.css";

type DeltaStyle = { text: string; clr: string };

function deltaFloat(
    last: number | undefined,
    base: number | undefined,
    decimalsHint?: number,
): DeltaStyle {
    if (last === undefined || base === undefined) {
        return { text: "—", clr: "var(--muted)" };
    }
    const d = last - base;
    if (Math.abs(d) < 0.0001) return { text: "—", clr: "var(--muted)" };
    const decimals =
        decimalsHint ??
        (Math.abs(last) < 1 && Math.abs(base) < 1 ? 4 : 2);
    return {
        text: (d >= 0 ? "+" : "") + d.toFixed(decimals),
        clr: d > 0 ? "var(--up)" : "var(--down)",
    };
}

function deltaInt(last: number | undefined, base: number | undefined): DeltaStyle {
    if (last === undefined || base === undefined) {
        return { text: "—", clr: "var(--muted)" };
    }
    const d = last - base;
    if (Math.abs(d) < 1) return { text: "—", clr: "var(--muted)" };
    return {
        text: (d >= 0 ? "+" : "") + Math.round(d).toLocaleString(),
        clr: d > 0 ? "var(--up)" : "var(--down)",
    };
}

function deltaPctPoints(
    last: number | undefined,
    base: number | undefined,
): DeltaStyle {
    if (last === undefined || base === undefined) {
        return { text: "—", clr: "var(--muted)" };
    }
    const l = last <= 1.5 ? last * 100 : last;
    const b = base <= 1.5 ? base * 100 : base;
    const d = l - b;
    if (Math.abs(d) < 0.01) return { text: "—", clr: "var(--muted)" };
    return {
        text: (d >= 0 ? "+" : "") + d.toFixed(1) + "pp",
        clr: d > 0 ? "var(--up)" : "var(--down)",
    };
}

function formatIv(v: number | undefined): string {
    if (v === undefined) return "—";
    const pct = v <= 1.5 ? v * 100 : v;
    return pct.toFixed(1) + "%";
}

function formatTime(iso: string | undefined): string {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return "—";
    }
}

function DeltaCell({
    sinceAdded,
    sincePoll,
    fontSize = 11,
}: {
    sinceAdded: DeltaStyle;
    sincePoll?: DeltaStyle;
    fontSize?: number;
}) {
    return (
        <td
            style={{
                padding: "6px 10px",
                textAlign: "end",
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                verticalAlign: "middle",
            }}
        >
            <div
                style={{
                    fontSize,
                    color: sinceAdded.clr,
                    fontWeight: 600,
                }}
            >
                {sinceAdded.text}
            </div>
            {sincePoll && sincePoll.text !== "—" && (
                <div
                    style={{
                        fontSize: 10,
                        color: sincePoll.clr,
                        opacity: 0.85,
                    }}
                >
                    ~{sincePoll.text}
                </div>
            )}
        </td>
    );
}

type RowAnalytics = { score?: number; bias?: string; whaleTotal?: number; flowBias?: number };

interface TrackedContractsViewProps {
    simpleLayout?: boolean;
    onSelectContract?: (c: TrackedContract) => void;
}

const TrackedContractsView: React.FC<TrackedContractsViewProps> = ({
    simpleLayout = false,
    onSelectContract,
}) => {
    const {
        trackedContracts,
        removeContract,
        refreshStatus,
        refreshFromData,
        isRefreshing,
        lastRefreshAt,
        lastRefreshError,
    } = useContractTracking();
    useTheme();
    const { language } = useLanguage();
    const { openSummary } = useContractSummary();
    const { openHistorical } = useHistoricalModal();
    const [rowAnalytics, setRowAnalytics] = useState<Record<string, RowAnalytics>>({});
    const fetchGen = useRef(0);
    const [sortBy, setSortBy] = useState<
        "symbol" | "strike" | "expiry" | "added"
    >("added");

    useEffect(() => {
        const handler = () => {
            try {
                const ds = (window as any).__mmecDataService;
                if (ds?.data?.length) {
                    refreshFromData(ds.data);
                }
            } catch {}
        };
        window.addEventListener("stableDataFetched", handler);
        window.addEventListener("dataUpdated", handler);
        return () => {
            window.removeEventListener("stableDataFetched", handler);
            window.removeEventListener("dataUpdated", handler);
        };
    }, [refreshFromData]);

    useEffect(() => {
        if (trackedContracts.length === 0) return;
        const gen = ++fetchGen.current;
        const batch = trackedContracts.slice(0, 50);
        let idx = 0;
        const concurrency = 10;
        const next: Record<string, RowAnalytics> = {};

        const runOne = async (c: TrackedContract) => {
            const q = new URLSearchParams({
                symbol: c.symbol,
                strike: String(c.strike),
                expiry: c.expiry.slice(0, 10),
                optionType: c.optionType,
            });
            try {
                const res = await fetch(
                    getApiUrl(`/api/contract-analytics/summary?${q}`),
                );
                if (!res.ok || gen !== fetchGen.current) return;
                const json = await res.json();
                const score = json.score as MmecScore | undefined;
                const whale = json.whale?.summary as WhaleSummary | undefined;
                next[c.id] = {
                    score: score?.score,
                    bias: score?.bias,
                    whaleTotal: whale?.total_premium,
                    flowBias: whale?.flow_bias_pct,
                };
                setRowAnalytics((prev) => ({ ...prev, [c.id]: next[c.id] }));
            } catch {
                /* real empty ok */
            }
        };

        const pump = () => {
            const slice = batch.slice(idx, idx + concurrency);
            idx += concurrency;
            if (slice.length === 0) return;
            void Promise.all(slice.map(runOne)).then(() => {
                if (gen === fetchGen.current && idx < batch.length) pump();
            });
        };
        pump();
    }, [trackedContracts]);

    const sorted = useMemo(
        () =>
            [...trackedContracts].sort((a, b) => {
                switch (sortBy) {
                    case "symbol":
                        return a.symbol.localeCompare(b.symbol);
                    case "strike":
                        return a.strike - b.strike;
                    case "expiry":
                        return (a.expiry || "").localeCompare(b.expiry || "");
                    case "added":
                        return (b.addedAt || "").localeCompare(a.addedAt || "");
                    default:
                        return 0;
                }
            }),
        [trackedContracts, sortBy],
    );

    const bg = "var(--base)";
    const fg = "var(--txt)";
    const dim = "var(--muted)";
    const border = "var(--hair)";
    const green = "var(--up)";
    const red = "var(--down)";

    const thStyle: React.CSSProperties = {
        padding: "6px 10px",
        textAlign: "end",
        fontSize: 11,
        fontWeight: 600,
        color: dim,
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
    };

    const sortArrow: React.CSSProperties = {
        color: "var(--acc)",
        fontSize: 10,
        marginInlineStart: 4,
    };

    const tdMono: React.CSSProperties = {
        padding: "6px 10px",
        textAlign: "end",
        fontFamily: "var(--font-mono)",
        fontVariantNumeric: "tabular-nums",
        fontSize: 12.5,
        whiteSpace: "nowrap",
    };

    const ds = isDesignSystemOn();

    return (
        <div
            className="tcv"
            style={{
                width: "100%",
                height: "100%",
                overflow: "auto",
                padding: 24,
                background: bg,
                color: fg,
                fontFamily: "var(--font-ui)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 16,
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: fg }}>
                        {language === "ar"
                            ? "قائمة المراقبة"
                            : "Watchlist"}{" "}
                        ({trackedContracts.length}/50)
                    </h2>
                    <div style={{ fontSize: 11.5, color: dim, marginTop: 4 }}>
                        {language === "ar"
                            ? "الصف العلوي = منذ الإضافة · ~ = منذ آخر تحديث (٣٠ ث)"
                            : "Top line = since added · ~ = since last refresh (30s)"}
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: dim }}>
                        {isRefreshing
                            ? language === "ar"
                                ? "جاري التحديث…"
                                : "Refreshing…"
                            : lastRefreshAt
                              ? `${language === "ar" ? "آخر" : "Last"}: ${formatTime(lastRefreshAt)}`
                              : ""}
                        {lastRefreshError ? (
                            <span style={{ color: red, marginInlineStart: 8 }}>
                                {lastRefreshError}
                            </span>
                        ) : null}
                    </span>
                    <button
                        onClick={() => refreshStatus(true)}
                        disabled={isRefreshing}
                        className="mmec-btn"
                        style={{
                            cursor: isRefreshing ? "wait" : "pointer",
                            opacity: isRefreshing ? 0.6 : 1,
                        }}
                    >
                        🔄 {language === "ar" ? "تحديث" : "Refresh"}
                    </button>
                </div>
            </div>

            {trackedContracts.length === 0 && ds ? (
                <ToolState
                    kind="empty"
                    lang={language === "ar" ? "ar" : "en"}
                    title={language === "ar" ? "لا عقود في قائمة المراقبة" : "No watchlist contracts"}
                    body={language === "ar" ? "انقر ☆ في جدول Stable أو Flow لإضافة عقد" : "Click ☆ on Stable or Flow rows to add one"}
                />
            ) : trackedContracts.length === 0 ? (
                <div style={{ textAlign: "center", padding: 48, color: dim }}>
                    <div style={{ fontSize: 28, marginBottom: 12, color: "var(--muted-2)" }}>⭐</div>
                    <p style={{ fontSize: 13, margin: 0 }}>
                        {language === "ar"
                            ? "لا عقود في قائمة المراقبة — انقر ☆ في جدول Stable أو Flow"
                            : "No watchlist contracts — click ☆ on Stable or Flow rows"}
                    </p>
                </div>
            ) : (
                <div
                    className="tcv-wrap"
                    style={{
                        overflowX: "auto",
                        borderRadius: "var(--radius)",
                        border: `1px solid ${border}`,
                        background: "var(--elev)",
                    }}
                >
                    <table
                        className="tcv-table"
                        style={{
                            width: "100%",
                            minWidth: simpleLayout ? 900 : 1280,
                            borderCollapse: "collapse",
                            fontSize: 12.5,
                        }}
                    >
                        <thead>
                            <tr
                                style={{
                                    borderBottom: `1px solid ${border}`,
                                    color: dim,
                                }}
                            >
                                <th
                                    style={{
                                        ...thStyle,
                                        textAlign: "start",
                                        cursor: "pointer",
                                        color: sortBy === "symbol" ? fg : dim,
                                    }}
                                    onClick={() => setSortBy("symbol")}
                                >
                                    Symbol {sortBy === "symbol" ? <span style={sortArrow}>▾</span> : ""}
                                </th>
                                <th style={{ ...thStyle, textAlign: "start" }}>
                                    Type
                                </th>
                                <th
                                    style={{
                                        ...thStyle,
                                        cursor: "pointer",
                                        color: sortBy === "strike" ? fg : dim,
                                    }}
                                    onClick={() => setSortBy("strike")}
                                >
                                    Strike {sortBy === "strike" ? <span style={sortArrow}>▾</span> : ""}
                                </th>
                                <th
                                    style={{
                                        ...thStyle,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        color: sortBy === "expiry" ? fg : dim,
                                    }}
                                    onClick={() => setSortBy("expiry")}
                                >
                                    Expiry {sortBy === "expiry" ? <span style={sortArrow}>▾</span> : ""}
                                </th>
                                <th style={thStyle}>Price</th>
                                <th style={thStyle}>Δ Price</th>
                                <th style={thStyle}>Volume</th>
                                <th style={thStyle}>Δ Vol</th>
                                <th style={thStyle}>OI</th>
                                <th style={thStyle}>Δ OI</th>
                                <th style={thStyle}>Bid/Ask</th>
                                <th style={thStyle}>Delta</th>
                                <th style={thStyle}>Δ Δ</th>
                                <th style={thStyle}>IV</th>
                                <th style={thStyle}>Δ IV</th>
                                <th style={thStyle} title="MMEC Score">Score</th>
                                <th style={thStyle} title="Whale premium">Whale $</th>
                                <th style={thStyle}>Flow</th>
                                <th
                                    style={{
                                        ...thStyle,
                                        textAlign: "center",
                                    }}
                                >
                                    Updated
                                </th>
                                <th style={{ width: 72 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((c: TrackedContract) => {
                                const pAdd = deltaFloat(
                                    c.lastPrice,
                                    c.snapshotPrice,
                                );
                                const pPoll = deltaFloat(
                                    c.lastPrice,
                                    c.pollPrice,
                                );
                                const vAdd = deltaInt(
                                    c.lastVolume,
                                    c.snapshotVolume,
                                );
                                const vPoll = deltaInt(
                                    c.lastVolume,
                                    c.pollVolume,
                                );
                                const oiAdd = deltaInt(c.lastOI, c.snapshotOI);
                                const oiPoll = deltaInt(c.lastOI, c.pollOI);
                                const dAdd = deltaFloat(
                                    c.lastDelta,
                                    c.snapshotDelta,
                                    3,
                                );
                                const dPoll = deltaFloat(
                                    c.lastDelta,
                                    c.pollDelta,
                                    3,
                                );
                                const ivAdd = deltaPctPoints(
                                    c.lastIV,
                                    c.snapshotIV,
                                );
                                const ivPoll = deltaPctPoints(
                                    c.lastIV,
                                    c.pollIV,
                                );
                                const changed =
                                    c.lastChangedFields &&
                                    c.lastChangedFields.length > 0;

                                const ra = rowAnalytics[c.id];
                                return (
                                    <tr
                                        key={c.id}
                                        onClick={() => {
                                            const ref = {
                                                symbol: c.symbol,
                                                strike: c.strike,
                                                expiry: c.expiry,
                                                optionType: c.optionType,
                                                id: c.id,
                                            };
                                            if (onSelectContract) onSelectContract(c);
                                            else openSummary(ref, { simpleLayout, tracked: c });
                                        }}
                                        style={{
                                            borderBottom: `1px solid ${border}`,
                                            cursor: "pointer",
                                        }}
                                    >
                                        <td
                                            style={{
                                                padding: "6px 10px",
                                                fontWeight: 600,
                                                color: fg,
                                            }}
                                        >
                                            {c.symbol}
                                            {changed ? (
                                                <span
                                                    title={c.lastChangedFields?.join(
                                                        ", ",
                                                    )}
                                                    style={{
                                                        marginInlineStart: 6,
                                                        fontSize: 8,
                                                        color: `var(--flow, ${green})`,
                                                    }}
                                                >
                                                    ●
                                                </span>
                                            ) : null}
                                        </td>
                                        <td style={{ padding: "6px 10px" }}>
                                            <span
                                                className="tcv-type"
                                                style={{
                                                    padding: "2px 8px",
                                                    borderRadius: "var(--radius-sm)",
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    letterSpacing: "0.03em",
                                                    background:
                                                        c.optionType === "C"
                                                            ? "var(--up-dim)"
                                                            : "var(--down-dim)",
                                                    color:
                                                        c.optionType === "C"
                                                            ? green
                                                            : red,
                                                }}
                                            >
                                                {c.optionType === "C"
                                                    ? "CALL"
                                                    : "PUT"}
                                            </span>
                                        </td>
                                        <td style={tdMono}>
                                            ${c.strike.toFixed(2)}
                                        </td>
                                        <td
                                            style={{
                                                ...tdMono,
                                                textAlign: "center",
                                                color: dim,
                                            }}
                                        >
                                            {c.expiry?.slice(0, 10) || "—"}
                                        </td>
                                        <td style={tdMono}>
                                            {c.lastPrice !== undefined
                                                ? `$${c.lastPrice.toFixed(2)}`
                                                : "—"}
                                        </td>
                                        <DeltaCell
                                            sinceAdded={pAdd}
                                            sincePoll={pPoll}
                                        />
                                        <td style={tdMono}>
                                            {c.lastVolume !== undefined
                                                ? c.lastVolume.toLocaleString()
                                                : "—"}
                                        </td>
                                        <DeltaCell
                                            sinceAdded={vAdd}
                                            sincePoll={vPoll}
                                        />
                                        <td style={tdMono}>
                                            {c.lastOI !== undefined
                                                ? c.lastOI.toLocaleString()
                                                : "—"}
                                        </td>
                                        <DeltaCell
                                            sinceAdded={oiAdd}
                                            sincePoll={oiPoll}
                                        />
                                        <td style={{ ...tdMono, fontSize: 11 }}>
                                            {(c.lastBid !== undefined ||
                                            c.lastAsk !== undefined) && ds ? (
                                                <>
                                                    <span className="tcv-bid">{c.lastBid?.toFixed(2) ?? "—"}</span>
                                                    /
                                                    <span className="tcv-ask">{c.lastAsk?.toFixed(2) ?? "—"}</span>
                                                </>
                                            ) : c.lastBid !== undefined ||
                                            c.lastAsk !== undefined ? (
                                                <>
                                                    {c.lastBid?.toFixed(2) ??
                                                        "—"}
                                                    /
                                                    {c.lastAsk?.toFixed(2) ??
                                                        "—"}
                                                </>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td style={tdMono}>
                                            {c.lastDelta !== undefined
                                                ? c.lastDelta.toFixed(3)
                                                : "—"}
                                        </td>
                                        <DeltaCell
                                            sinceAdded={dAdd}
                                            sincePoll={dPoll}
                                        />
                                        <td style={tdMono}>
                                            {formatIv(c.lastIV)}
                                        </td>
                                        <DeltaCell
                                            sinceAdded={ivAdd}
                                            sincePoll={ivPoll}
                                        />
                                        <td style={{ ...tdMono, fontWeight: 600 }}>
                                            {ra?.score != null ? (
                                                <span
                                                    style={{
                                                        color:
                                                            ra.score >= 58
                                                                ? green
                                                                : ra.score <= 42
                                                                  ? red
                                                                  : dim,
                                                    }}
                                                >
                                                    {ra.score}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td style={tdMono}>
                                            {ra?.whaleTotal != null
                                                ? `$${Math.round(ra.whaleTotal).toLocaleString()}`
                                                : "—"}
                                        </td>
                                        <td style={{ ...tdMono, fontSize: 11 }}>
                                            {ra?.flowBias != null
                                                ? `${ra.flowBias}% buy`
                                                : "—"}
                                        </td>
                                        <td
                                            style={{
                                                ...tdMono,
                                                textAlign: "center",
                                                fontSize: 10,
                                                color: dim,
                                            }}
                                            title={c.lastSource || undefined}
                                        >
                                            {formatTime(c.lastUpdated)}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openHistorical(
                                                        {
                                                            underlying: c.symbol,
                                                            strike: c.strike,
                                                            expiry: c.expiry.slice(0, 10),
                                                            optionType:
                                                                c.optionType === "P"
                                                                    ? "PUT"
                                                                    : "CALL",
                                                            source: "premium",
                                                        },
                                                        simpleLayout
                                                            ? { compactChrome: true }
                                                            : undefined,
                                                    );
                                                }}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    color: dim,
                                                    fontSize: 14,
                                                    marginInlineEnd: 4,
                                                }}
                                                title="History"
                                            >
                                                📜
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeContract(c.id);
                                                }}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    color: dim,
                                                    fontSize: 16,
                                                }}
                                                title="Remove"
                                            >
                                                ×
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div
                className="tcv-foot"
                style={{
                    marginTop: 16,
                    fontSize: 11.5,
                    color: dim,
                    lineHeight: 1.6,
                }}
            >
                {ds ? (
                    /* Design System preview: same explanation without vendor names (design rule). */
                    language === "ar" ? (
                        <>
                            <strong>كيف يعمل التحديث:</strong> كل ٣٠ ثانية يُحدَّث كل عقد من ذاكرة المنصة أولاً،
                            ثم من لقطة مباشرة للعقود الناقصة. يحدّث شريط Stable أيضاً عند كل جلب بيانات.
                            النقطة الملوّنة = حقول تغيّرت في آخر استعلام.
                        </>
                    ) : (
                        <>
                            <strong>How updates work:</strong> Every 30s each contract refreshes from the
                            platform cache first, then a live snapshot for anything missing. The Stable bar
                            also merges on each data fetch. Coloured dot = fields changed on the last poll.
                            History (last 60 points) is kept per contract in this browser for export.
                        </>
                    )
                ) : language === "ar" ? (
                    <>
                        <strong>كيف يعمل التحديث:</strong> كل ٣٠ ثانية → POST
                        /api/contracts/status (QuestDB ثم Theta للعقود الناقصة).
                        Stable bar يحدّث أيضاً عند كل جلب بيانات. النقاط الخضراء =
                        حقول تغيّرت في آخر استعلام.
                    </>
                ) : (
                    <>
                        <strong>How updates work:</strong> Every 30s → POST{" "}
                        <code>/api/contracts/status</code> (QuestDB cache first,
                        Theta snapshot for misses). Stable bar also merges on
                        each data fetch. Green dot = fields changed on last API
                        poll. History (last 60 points) stored per contract in
                        localStorage for export.
                    </>
                )}
            </div>
        </div>
    );
};

export default TrackedContractsView;
